/**
 * PRESSURE (doc 08 §Pressure) — the post-victory screen, per depth.
 *
 * Six toggles, up to six active, +20% Pearls each. It only opens on a depth
 * whose boss has already fallen, because that is the whole conceit: you have
 * beaten this place, so now you get to make it worse on purpose.
 */

import { S } from '../../content/strings.ts';
import { activeTiers, MAX_TIERS, PEARL_BONUS_PER_TIER, PRESSURE_TIERS } from '../../meta/pressure.ts';
import { font } from '../../render/font/font.ts';
import { PixelSurface } from '../../render/surface.ts';
import type { FrameOpts, MenuItem } from '../../ui/menu.ts';
import { wrapText } from '../../ui/menu.ts';
import { g, UI } from '../../ui/theme.ts';
import { drawPanel, drawStatRow, type Rect } from '../../ui/widgets.ts';
import type { App } from '../app.ts';
import { DepthScreen } from './depth.ts';
import { MenuScreen } from './menuscreen.ts';

const DEPTH_NAMES: Record<number, string> = { 1: S.pressure.shelf, 2: S.pressure.gloom, 3: S.pressure.floor };

export class PressureScreen extends MenuScreen {
  private readonly depth: number;

  constructor(app: App, depth: number) {
    super(app);
    this.depth = depth;
  }

  private get active(): string[] {
    const p = this.app.profile;
    if (!p) return [];
    return (p.pressure[String(this.depth)] ??= []);
  }

  private on(id: string): boolean {
    return this.active.includes(id);
  }

  protected frame(): FrameOpts {
    const tiers = activeTiers(this.active).length;
    return {
      title: `PRESSURE · D${this.depth}`,
      subtitle: S.pressure.choseThisWe,
      footer: S.pressure.enterTogglesEsc,
      stamp: tiers > 0 ? `+${Math.round(PEARL_BONUS_PER_TIER * tiers * 100)}% PEARLS` : S.pressure.noPressure,
    };
  }

  protected override columnWidth(body: Rect): number {
    return Math.min(340, Math.floor(body.w * 0.52));
  }

  protected items(): MenuItem[] {
    const tiers = activeTiers(this.active).length;
    return PRESSURE_TIERS.map((t) => {
      const on = this.on(t.id);
      const item: MenuItem = {
        label: t.name,
        hint: t.effect,
        value: on ? 'ON' : S.pressure.off,
      };
      if (on) item.tone = 'critical';
      // the cap is a cap: an inactive tier at six is not a choice, it is a wall
      if (!on && tiers >= MAX_TIERS) {
        item.disabled = true;
        item.value = `MAX ${MAX_TIERS}`;
      }
      return item;
    });
  }

  protected activate(index: number): void {
    const tier = PRESSURE_TIERS[index];
    const profile = this.app.profile;
    if (!tier || !profile) return;
    const list = this.active;
    const at = list.indexOf(tier.id);
    if (at >= 0) list.splice(at, 1);
    else if (activeTiers(list).length < MAX_TIERS) list.push(tier.id);
    else return;
    if (list.length === 0) delete profile.pressure[String(this.depth)];
    this.app.persist();
    this.dirty = true;
  }

  protected override back(): void {
    this.app.go((a) => new DepthScreen(a));
  }

  /** What the stack is actually doing, in the player's own words. */
  protected override detail(s: PixelSurface, body: Rect): void {
    const x = body.x + this.columnWidth(body) + g(6);
    const w = Math.min(300, body.x + body.w - x);
    if (w < 150) return;
    const tier = PRESSURE_TIERS[this.selected];
    if (!tier) return;

    let y = body.y;
    font('header').draw(s, tier.name, x, y, UI.ink, { caps: true, letterSpacing: 1 });
    y += font('header').capHeight + g(3);
    for (const line of wrapText('micro', tier.line, w)) {
      font('micro').draw(s, line, x, y, UI.inkDim, { caps: true, letterSpacing: 1 });
      y += font('micro').capHeight + g(2);
    }
    y += g(3);
    for (const line of wrapText('body', tier.effect, w)) {
      font('body').draw(s, line, x, y, this.on(tier.id) ? UI.critical : UI.ink, { caps: true, letterSpacing: 1 });
      y += font('body').capHeight + g(2);
    }

    const tiers = activeTiers(this.active).length;
    y += g(6);
    const bottom = body.y + body.h;
    if (y + 40 > bottom) return;
    drawPanel(s, { x, y, w, h: 38 });
    drawStatRow(s, { x: x + g(3), y: y + g(3), w: w - g(6), h: 8 }, S.pressure.tiersActive, `${tiers} / ${MAX_TIERS}`);
    drawStatRow(
      s,
      { x: x + g(3), y: y + g(3) + 12, w: w - g(6), h: 8 },
      S.pressure.pearlsFrom,
      `${DEPTH_NAMES[this.depth] ?? `D${this.depth}`} ×${(1 + PEARL_BONUS_PER_TIER * tiers).toFixed(1)}`,
    );
  }
}
